// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {OAuthAuthorizeModal} from '@app/features/auth/components/modals/OAuthAuthorizeModal';
import styles from '@app/features/discovery/components/modals/AppDetailModal.module.css';
import type {DiscoveryApp} from '@app/features/discovery/state/AppDiscoveryState';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {
	ArrowSquareOutIcon,
	CheckCircleIcon,
	GlobeIcon,
	PlusCircleIcon,
	ShieldCheckIcon,
	UsersThreeIcon,
} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useCallback, useState} from 'react';

interface AppDetailModalProps {
	readonly app: DiscoveryApp;
}

export const AppDetailModal = observer(function AppDetailModal({app}: AppDetailModalProps) {
	const [activeTab, setActiveTab] = useState<'overview' | 'commands' | 'info'>('overview');

	const handleAddServer = useCallback(() => {
		if (app.customInstallUrl) {
			window.open(app.customInstallUrl, '_blank', 'noopener,noreferrer');
			return;
		}
		ModalCommands.pushWithKey(
			modal(() => (
				<OAuthAuthorizeModal
					search={`?client_id=${app.clientId}&scope=bot%20applications.commands&permissions=${app.permissions || '8'}`}
					data-flx="discovery.apps.oauth-modal"
				/>
			)),
			`oauth-authorize:${app.clientId}`,
		);
	}, [app]);

	return (
		<Modal.Root size="medium" centered onClose={ModalCommands.pop} data-flx="discovery.app-detail-modal">
			<div className={styles.container}>
				<div
					className={styles.banner}
					style={{
						background: app.banner || 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)',
					}}
				/>

				<div className={styles.headerContent}>
					<div className={styles.avatarWrapper}>
						<img src={app.avatar} alt={app.name} className={styles.avatar} />
						{app.verified && (
							<div className={styles.verifiedCheck} title="Verified Application">
								<CheckCircleIcon size={20} weight="fill" color="#5865f2" />
							</div>
						)}
					</div>

					<div className={styles.actionButtons}>
						<button
							type="button"
							className={styles.installBtn}
							onClick={handleAddServer}
							data-flx="discovery.app-detail.add-server-btn"
						>
							<PlusCircleIcon size={18} weight="bold" />
							<span>Add to Server</span>
						</button>
					</div>
				</div>

				<div className={styles.titleSection}>
					<h2 className={styles.appName}>
						{app.name}
						{app.verified && <ShieldCheckIcon size={22} weight="fill" color="#5865f2" />}
					</h2>
					<p className={styles.tagline}>{app.tagline}</p>

					<div className={styles.metaRow}>
						<span className={styles.categoryBadge}>{app.category}</span>
						<div className={styles.metaItem}>
							<UsersThreeIcon size={16} />
							<span>{app.stats.serverCount.toLocaleString()} servers</span>
						</div>
						<div className={styles.metaItem}>
							<span>By <strong>{app.developer.name}</strong></span>
						</div>
					</div>
				</div>

				<div className={styles.tabBar} role="tablist">
					<button
						type="button"
						role="tab"
						aria-selected={activeTab === 'overview'}
						className={`${styles.tabItem} ${activeTab === 'overview' ? styles.tabItemActive : ''}`}
						onClick={() => setActiveTab('overview')}
					>
						Overview
					</button>
					<button
						type="button"
						role="tab"
						aria-selected={activeTab === 'commands'}
						className={`${styles.tabItem} ${activeTab === 'commands' ? styles.tabItemActive : ''}`}
						onClick={() => setActiveTab('commands')}
					>
						Slash Commands ({app.commands.length})
					</button>
					<button
						type="button"
						role="tab"
						aria-selected={activeTab === 'info'}
						className={`${styles.tabItem} ${activeTab === 'info' ? styles.tabItemActive : ''}`}
						onClick={() => setActiveTab('info')}
					>
						Developer & Links
					</button>
				</div>

				<div className={styles.bodyContent}>
					{activeTab === 'overview' && (
						<div>
							<p className={styles.descriptionText}>{app.description}</p>

							{app.tags.length > 0 && (
								<>
									<h4 className={styles.sectionHeader}>Tags</h4>
									<div className={styles.tagPills}>
										{app.tags.map((tag) => (
											<span key={tag} className={styles.tagPill}>
												#{tag}
											</span>
										))}
									</div>
								</>
							)}

							<h4 className={styles.sectionHeader}>Resources</h4>
							<div className={styles.linksList}>
								{app.supportGuildUrl && (
									<a
										href={app.supportGuildUrl}
										target="_blank"
										rel="noopener noreferrer"
										className={styles.externalLink}
									>
										<UsersThreeIcon size={16} />
										<span>Join Support Server</span>
										<ArrowSquareOutIcon size={14} />
									</a>
								)}
								{app.websiteUrl && (
									<a
										href={app.websiteUrl}
										target="_blank"
										rel="noopener noreferrer"
										className={styles.externalLink}
									>
										<GlobeIcon size={16} />
										<span>Official Website</span>
										<ArrowSquareOutIcon size={14} />
									</a>
								)}
							</div>
						</div>
					)}

					{activeTab === 'commands' && (
						<div className={styles.commandsList}>
							{app.commands.map((cmd) => (
								<div key={cmd.name} className={styles.commandItem}>
									<span className={styles.commandName}>{cmd.name}</span>
									<span className={styles.commandDesc}>{cmd.description}</span>
								</div>
							))}
						</div>
					)}

					{activeTab === 'info' && (
						<div>
							<div className={styles.commandsList}>
								<div className={styles.commandItem}>
									<span className={styles.commandName}>Developer</span>
									<span className={styles.commandDesc}>
										{app.developer.name} {app.developer.verified && '(Verified Publisher)'}
									</span>
								</div>
								<div className={styles.commandItem}>
									<span className={styles.commandName}>Application ID</span>
									<span className={styles.commandDesc}>{app.clientId}</span>
								</div>
								<div className={styles.commandItem}>
									<span className={styles.commandName}>Permissions Bitfield</span>
									<span className={styles.commandDesc}>{app.permissions}</span>
								</div>
							</div>

							<h4 className={styles.sectionHeader}>Legal & Privacy Policies</h4>
							<div className={styles.linksList}>
								{app.termsOfServiceUrl && (
									<a
										href={app.termsOfServiceUrl}
										target="_blank"
										rel="noopener noreferrer"
										className={styles.externalLink}
									>
										<span>Terms of Service</span>
										<ArrowSquareOutIcon size={14} />
									</a>
								)}
								{app.privacyPolicyUrl && (
									<a
										href={app.privacyPolicyUrl}
										target="_blank"
										rel="noopener noreferrer"
										className={styles.externalLink}
									>
										<span>Privacy Policy</span>
										<ArrowSquareOutIcon size={14} />
									</a>
								)}
							</div>
						</div>
					)}
				</div>
			</div>
		</Modal.Root>
	);
});
